const ALLOWED_INLINE_TAGS = /^(b|strong|i|em|u|br|a)$/i;

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalizeDescriptionInput(description) {
  return String(description)
    .replace(/\r\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<\/?p[^>]*>/gi, "\n")
    .replace(/<\/div>\s*<div[^>]*>/gi, "\n")
    .replace(/<\/?div[^>]*>/gi, "\n")
    .replace(/<\/li>\s*<li[^>]*>/gi, "\n")
    .replace(/<\/?li[^>]*>/gi, "\n")
    .replace(/<\/?ul[^>]*>/gi, "\n")
    .replace(/&nbsp;/gi, " ");
}

function stripHtmlTags(text) {
  return String(text || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}

function containsHtml(text) {
  return /<[a-z][^>]*>/i.test(String(text || ""));
}

function sanitizeInlineHtml(text) {
  let html = String(text || "");

  html = html.replace(/<b(\s[^>]*)?>/gi, "<strong>");
  html = html.replace(/<\/b>/gi, "</strong>");
  html = html.replace(/<i(\s[^>]*)?>/gi, "<em>");
  html = html.replace(/<\/i>/gi, "</em>");

  html = html.replace(
    /<a\s+([^>]*?)href\s*=\s*["']([^"']+)["']([^>]*)>/gi,
    (_match, _before, href) => {
      const safeUrl = String(href).trim();
      if (!/^https?:\/\//i.test(safeUrl)) {
        return "";
      }
      return `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">`;
    }
  );

  html = html.replace(/<\/?([a-z][a-z0-9]*)([^>]*)>/gi, (match, tagName) => {
    if (ALLOWED_INLINE_TAGS.test(tagName)) {
      if (tagName.toLowerCase() === "br") {
        return "<br>";
      }
      return match.toLowerCase().startsWith("</")
        ? `</${tagName.toLowerCase()}>`
        : `<${tagName.toLowerCase()}>`;
    }
    return "";
  });

  return html.trim();
}

function applyInlineFormatting(text) {
  if (containsHtml(text)) {
    return sanitizeInlineHtml(text);
  }

  let html = escapeHtml(text);
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => {
    const safeUrl = String(url).trim();
    if (!/^https?:\/\//i.test(safeUrl)) {
      return escapeHtml(`${label} (${url})`);
    }
    return `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");
  html = html.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "<em>$1</em>");
  return html;
}

function plainText(text) {
  let normalized = stripHtmlTags(text);
  normalized = normalized
    .replace(/\*\*|__/g, "")
    .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "$1")
    .trim();
  return normalized;
}

function isPriceOnlyLine(line) {
  const text = plainText(line);
  return /^\$[\d,.]+(\s+per\s+\w+)?$/i.test(text);
}

function formatRateFromParts(labelLine, priceLine) {
  const price = plainText(priceLine);
  const labelRaw = String(labelLine || "").trim();
  if (!labelRaw || !price) {
    return null;
  }

  return {
    labelHtml: emphasizePlainLabel(
      applyInlineFormatting(labelRaw),
      labelRaw
    ),
    price,
  };
}

function emphasizePlainLabel(labelHtml, labelRaw) {
  if (/<strong>|<b>/i.test(labelHtml)) {
    return labelHtml;
  }

  const plain = stripHtmlTags(labelRaw);
  const match = plain.match(/^(.+?)(\s+(?:\d|–|-).*)$/);
  if (match && match[1].trim()) {
    const tail = applyInlineFormatting(match[2]);
    return `<strong>${escapeHtml(match[1].trim())}</strong>${tail}`;
  }

  return `<strong>${labelHtml}</strong>`;
}

function formatRateLine(line) {
  const normalized = plainText(line);
  const separator = normalized.lastIndexOf(" - ");
  if (separator <= 0 || !/\$\d/.test(normalized)) {
    return null;
  }

  const price = normalized.slice(separator + 3).trim();
  const priceSuffix = ` - ${price}`;
  const labelEndIndex = line.lastIndexOf(priceSuffix);
  const labelRaw =
    labelEndIndex >= 0
      ? line.slice(0, labelEndIndex).trim()
      : normalized.slice(0, separator).trim();

  return {
    labelHtml: emphasizePlainLabel(
      applyInlineFormatting(labelRaw),
      labelRaw
    ),
    price,
  };
}

function pushRateBlock(blocks, rate) {
  blocks.push(
    `<div class="venue-desc__rate"><span class="venue-desc__rate-label">${rate.labelHtml}</span><span class="venue-desc__rate-price">${escapeHtml(rate.price)}</span></div>`
  );
}

export function formatVenueDescription(description) {
  if (!description) {
    return "";
  }

  const normalized = normalizeDescriptionInput(description);
  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const blocks = [];
  let listOpen = false;

  const closeList = () => {
    if (listOpen) {
      blocks.push("</ul>");
      listOpen = false;
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const nextLine = lines[index + 1];

    if (isPriceOnlyLine(line)) {
      continue;
    }

    if (nextLine && isPriceOnlyLine(nextLine)) {
      const pairedRate = formatRateFromParts(line, nextLine);
      if (pairedRate) {
        closeList();
        pushRateBlock(blocks, pairedRate);
        index += 1;
        continue;
      }
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      closeList();
      const level = headingMatch[1].length;
      blocks.push(
        `<h${level} class="venue-desc__heading">${applyInlineFormatting(headingMatch[2])}</h${level}>`
      );
      continue;
    }

    if (/^[-*•]\s+/.test(plainText(line)) || /^[-*•]\s+/.test(line)) {
      if (!listOpen) {
        blocks.push('<ul class="venue-desc__list">');
        listOpen = true;
      }
      const listText = plainText(line).replace(/^[-*•]\s+/, "");
      blocks.push(`<li>${applyInlineFormatting(listText)}</li>`);
      continue;
    }

    closeList();

    const rate = formatRateLine(line);
    if (rate) {
      pushRateBlock(blocks, rate);
      continue;
    }

    blocks.push(`<p class="venue-desc__para">${applyInlineFormatting(line)}</p>`);
  }

  closeList();
  return blocks.join("");
}

export function hasVenueDescriptionContent(description) {
  return Boolean(stripHtmlTags(normalizeDescriptionInput(description)));
}
